import { useState } from "react";
import classNames from "classnames";
import ReactMarkdown from "markdown-to-jsx";

import Button from "components/buttonNew/button";
import IconButton from "components/iconButton/iconButton";
import { CheckmarkIcon, ChevronIcon } from "components/icons/icons";

import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { useMixpanelCheckoutTracking } from "lib/hooks/useMixpanelCheckoutTracking";
import { useNavigationManager } from "lib/hooks/useNavigationManager";
import type { PlanTierType } from "content-ui/subscribe/fetcher";
import { useUserData } from "lib/store/user";
import { toTierKey } from "lib/sharedUtils/planTiers";
import { isCheckoutUrl } from "lib/sharedUtils/checkoutLinks";

type PlanCardProps = PlanTierType & {
  className?: string;
};

const SINGLE_SEAT_OFFER_KEY = "singleseatprosubscription";

export const PlanCard = ({
  accentColour = "black",
  tier,
  publicTitle,
  price,
  priceSubtitle,
  ctaUrl,
  ctaText,
  ctaColour,
  className,
  benefit,
  additionalButtonLabel,
  additionalButtonUrl,
}: PlanCardProps) => {
  const [isMobileHidden, setIsMobileHidden] = useState(true);
  const { track } = useMixpanelTracking();
  const { trackCheckoutStarted } = useMixpanelCheckoutTracking();
  const { readerSubscriptionLevel } = useUserData();
  const { handlePurchaseButtonClick, redirectToLoginForPurchase } =
    useNavigationManager();
  const isSingleSeatOffer = toTierKey(tier) === SINGLE_SEAT_OFFER_KEY;
  const isUnregistered = readerSubscriptionLevel === "unregistered";
  const hasLink = !!ctaUrl?.trim();
  const heading = publicTitle?.trim() || tier;

  const onPurchaseButtonClick = async () => {
    if (isSingleSeatOffer) {
      await trackCheckoutStarted("subscribe_page", tier);
      handlePurchaseButtonClick();
      return;
    }

    if (hasLink && isCheckoutUrl(ctaUrl)) {
      await trackCheckoutStarted("subscribe_page", tier, {
        sendImmediately: true,
      });
    }

    if (isUnregistered) {
      redirectToLoginForPurchase();
    }
  };

  const onPrimaryButtonClick = async () => {
    await track("Pricing card button click", {
      "Click text": ctaText,
      "Click link": ctaUrl,
    });
    await onPurchaseButtonClick();
  };

  const onAdditionalButtonClick = async () => {
    await track("Pricing card additional button click", {
      "Click text": additionalButtonLabel,
      "Click link": additionalButtonUrl,
    });
  };

  const onCollapseToggle = () => {
    setIsMobileHidden((oldValue) => !oldValue);
  };

  return (
    <article
      className={classNames(
        className,
        "flex flex-col rounded-[24px] bg-white px-4 py-5 text-center md:h-100 lg:h-100"
      )}
    >
      <div className={"flex flex-row"}>
        <div
          className={
            "flex w-full flex-col items-center gap-x-4 gap-y-[10px] [grid-template-areas:'title''price''subtitle''button'] md:[grid-template-areas:'title_button''price_button''subtitle_subtitle'] lg:[grid-template-areas:'title''price''subtitle''button']"
          }
        >
          <div
            className={classNames(
              "md:h flex items-end [grid-area:title] lg:h-[78px]"
            )}
          >
            <h2
              className={classNames(
                "md:text font-bold font-recklessBold text-[26px]",
                {
                  "text-pro-blue": accentColour === "navy",
                  "text-cerise": accentColour === "cerise",
                  "text-black-rock": accentColour === "black",
                }
              )}
            >
              {heading}
            </h2>
          </div>
          <p className="text-center font-bold text-[16px] leading-[130%] text-mono-40 [grid-area:price] lg:text-center">
            {price}
          </p>
          <p
            className={classNames(
              "text-center text-[16px] leading-[130%] [grid-area:subtitle] sm:min-h-fit md:min-h-[48px] lg:mb-5 lg:h-[42px] lg:text-center",
              {
                "text-pro-blue": accentColour === "navy",
                "text-cerise": accentColour === "cerise",
                "text-black-rock": accentColour === "black",
              }
            )}
          >
            {priceSubtitle}
          </p>
          <div
            className={
              "mt-[10px] w-full [grid-area:button] [grid-column-start:auto] md:mt-0 md:[grid-column-start:none] lg:[grid-column-start:auto]"
            }
          >
            <Button
              size={"medium"}
              className={"w-full md:mt-3"}
              colour={ctaColour}
              href={ctaUrl}
              component={isSingleSeatOffer && hasLink ? "button" : "link"}
              onClick={onPrimaryButtonClick}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </div>
      <hr className="my-4 border-mono-10" />
      <div className="flex h-100 w-100 flex-col justify-between bg-white">
        <div className="">
          <ul
            className="mb-[20px] flex flex-col gap-y-[10px]"
            aria-label={"Benefits list"}
          >
            {benefit.map((point, i) => {
              return (
                <li
                  aria-label={"Benefit"}
                  key={i}
                  className={classNames(
                    "flex-row items-start gap-[6px] text-left",
                    {
                      "hidden md:flex": isMobileHidden && i > 2,
                      flex: !isMobileHidden || i <= 2,
                    }
                  )}
                >
                  <div>
                    <CheckmarkIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <ReactMarkdown
                      className={classNames(
                        "text-[16px] leading-[130%] text-mono-70",
                        "[&_a]:inline [&_a]:underline [&_a]:[text-decoration-skip-ink:none] [&_a]:[text-underline-offset:2px]",
                        "focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring focus-visible:[&_a]:ring-black-rock"
                      )}
                      options={{ forceBlock: true }}
                    >
                      {point.multilineString}
                    </ReactMarkdown>
                  </div>
                </li>
              );
            })}
          </ul>
          {benefit.length > 3 && (
            <div className={"md:hidden"}>
              <IconButton
                label={isMobileHidden ? "See more" : "Close"}
                labelPosition={"start"}
                component={"button"}
                onClick={onCollapseToggle}
              >
                <div className={"flex h-[24px] w-[24px] items-center"}>
                  <ChevronIcon
                    className={classNames(
                      "transition-transform duration-200",
                      !isMobileHidden && "rotate-180"
                    )}
                  />
                </div>
              </IconButton>
            </div>
          )}
        </div>
        {additionalButtonUrl && additionalButtonLabel && (
          <div className="justify-end px-[14px] py-[20px]">
            <Button
              size="small"
              buttonType={"secondary"}
              colour={"black"}
              className="lg:w-100"
              href={additionalButtonUrl}
              component={"link"}
              onClick={onAdditionalButtonClick}
            >
              {additionalButtonLabel}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};
